import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  NavLink,
  useParams,
  Outlet,
  generatePath,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  IconSettings,
  IconArrowLeft,
  ButtonSecondary,
  IconDownload,
  ButtonSize,
  Table,
  Dropzone,
  useQueryState,
  Popup,
  useGetList,
  IconSelected,
  PermissionEnum,
  SchemaFilterType,
  IconChangeLoClock,
  ResponseError,
  getResponseErrors,
  formatDate,
  Filters,
  // IconUpload,
  IconExpand,
  IconTrash,
  IconDropzoneAlternative,
  Checkbox,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearBlacklistState,
  setSkipValue,
  // setSelectedListIds,
} from './store/slices/item';
import { blacklistUsersColumns, blacklistUsersSchema } from './variables/user';
import {
  useLazyGetBlacklistQuery,
  useLazyGetBlacklistDataQuery,
  useGetBlacklistTemplateQuery,
  useUploadBlacklistDataMutation,
} from './store/services';
import { BlacklistData } from '../types';

import ROUTES from '../../routes';

import cn from './blacklist.module.css';

const formatBlacklistListData = (data: BlacklistData[]): BlacklistData[] => {
  const nextList = data?.map((item) => {
    const result = { ...item };
    const currentUser = data?.find((user) => user.id === result.id);

    result.url = '';

    if (currentUser) {
      result.url = generatePath(ROUTES.BLACKLIST_ITEM_USER_INFO, {
        itemId: currentUser.source?.blacklistId,
        userId: result.id,
      });
    }

    const render: Record<string, React.JSX.Element> = {};

    render.createdAt = (
      <div>{item.createdAt ? formatDate(item.createdAt, true) : '—'}</div>
    );
    render.lastUpdated = (
      <div>{item?.lastUpdated ? formatDate(item.lastUpdated, true) : '—'}</div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const BlacklistItemPage = () => {
  const [dropzoneVisible, setDropzoneVisible] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [takeCount, setTakeCount] = useState(0);
  const [errors, setErrors] = useState('');
  const [clearListPopupVisible, setClearListPopupVisible] = useState(false);
  const [deleteRecordsPopupVisible, setDeleteRecordsPopupVisible] =
    useState(false);
  const [selectedUploadType, setSelectedUploadType] = useState<
    'new' | 'replace'
  >('new');

  const {
    blacklist,
    // template,
    uploadedRows,
    values,
    pagination,
    skipValue,
    selectedListIds,
    errorsList,
  } = useAppSelector((state) => state.blacklist.item);

  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const tableRef = useRef<HTMLInputElement | null>(null);

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const { itemId } = useParams();

  const { sort, onSortChange, onFilterChange, filters } =
    useQueryState(blacklistUsersSchema);

  useGetBlacklistTemplateQuery();
  const [getBlacklist, { isLoading, isFetching: blacklistIsFetching }] =
    useLazyGetBlacklistQuery();
  const [getBlacklistData, { isLoading: dataIsLoading, isFetching }] =
    useLazyGetBlacklistDataQuery();
  const [uploadBlacklist, { isLoading: uploadInProgress, isSuccess }] =
    useUploadBlacklistDataMutation();

  const { getListAction, onSort, setPage, errorList } = useGetList({
    getList: getBlacklistData,
    onFilterChange,
    onSortChange,
    itemId,
    schema: blacklistUsersSchema,
  });

  const onUpload = (data: FileList) => {
    const file = data[0];

    const body = {
      id: itemId as string,
      file,
    };

    uploadBlacklist(body);
  };

  const formattedData = useMemo(() => {
    const data = formatBlacklistListData(values as BlacklistData[]);

    return data;
  }, [values]);

  const getBlacklistAction = useCallback(
    async (blacklistId: string) => {
      const response = (await getBlacklist({
        id: blacklistId,
        query: 'include=settings',
      })) as { error?: ResponseError };

      if (response?.error) {
        getResponseErrors({
          error: response?.error,
          setErrorList: setErrors,
        });
      }

      if (activePermissions?.includes(PermissionEnum.BLACKLISTS_ITEMS_LIST)) {
        let skipNumber;

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.cursor) {
            skipNumber = Number(parsed.cursor);
          } else {
            skipNumber = 0;
          }
        } else {
          skipNumber = 0;
        }

        dispatch(setSkipValue(skipNumber));

        if (takeCount !== 0 && skipNumber !== undefined) {
          const nextFilters: Filters = {
            ...filters,
            take: {
              value: [takeCount.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          if (skipNumber > 0) {
            nextFilters.cursor = {
              value: [skipNumber.toString()],
              type: SchemaFilterType.STRING,
            };
          }

          getListAction({
            filters: nextFilters,
          });
        }
      }
    },
    [filters, sort],
  );

  const onCloseUploadPopup = () => {
    setPopupVisible(false);

    getListAction({
      filters,
    });
  };

  // const onRowsSelect = (rows: string[]) => {
  //   dispatch(setSelectedListIds(rows));
  // };

  // const downloadTemplate = () => {
  //   const element = document.createElement('a');
  //   element.href = `data:text/csv;charset=utf-8,${encodeURI(
  //     template as string,
  //   )}`;
  //   element.target = '_blank';
  //   element.download = 'Template list.csv';
  //   element.click();
  // };

  useEffect(() => {
    if (itemId && !blacklist && takeCount !== 0) {
      getBlacklistAction(itemId);
    }
  }, [itemId, getBlacklistAction, takeCount]);

  useEffect(() => {
    if (tableRef && tableRef.current) {
      const { offsetHeight } = tableRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        setTakeCount(Math.ceil(take + 3));
      }
    }
  }, [tableRef]);

  useEffect(() => {
    if (isSuccess) {
      setDropzoneVisible(false);
      setPopupVisible(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    let title = blacklist?.name || 'Block list';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [blacklist?.name]);

  let content: React.JSX.Element;

  if (isLoading || dataIsLoading) {
    content = (
      <div className={cn.blacklistPageContentLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        {!errors && (
          <>
            <div className={cn.blacklistPageTop}>
              <div className={cn.blacklistPageTitle}>
                {blacklist?.name}
                {blacklist && (
                  <div
                    className={cn.lastSyncDate}
                  >{`Last sync: ${blacklist?.successfulSyncLastDate ? formatDate(blacklist?.successfulSyncLastDate, true) : '—'}`}</div>
                )}
              </div>
              <div className={cn.blacklistPageControls}>
                <div className={cn.blacklistPageControlsLeft}>
                  {!blacklist?.source && (
                    <>
                      {activePermissions?.includes(
                        PermissionEnum.BLACKLISTS_ITEMS_CREATE_ACTION,
                      ) && (
                        <NavLink
                          className={`${cn.blacklistSettingsLink} ${cn.background}`}
                          to={generatePath(
                            `${ROUTES.BLACKLIST_ITEM_ADD_RECORD}${
                              search || ''
                            }`,
                            { itemId: itemId as string },
                          )}
                        >
                          <IconExpand />
                          Add record
                        </NavLink>
                      )}

                      {/* {activePermissions?.includes(
                        PermissionEnum.BLACKLISTS_EDIT_ACTION,
                      ) && (
                        <ButtonSecondary
                          size={ButtonSize.SMALL}
                          className={`button-white ${cn.blacklistPageControlButton} ${cn.blacklistPageControlButtonHidden}`}
                          text="Import records"
                          icon={<IconUpload />}
                          onClick={() => setDropzoneVisible(!dropzoneVisible)}
                        />
                      )} */}
                      {/* <ButtonSecondary
                        size={ButtonSize.SMALL}
                        className={`button-white ${cn.blacklistPageControlButton} ${cn.clearListControlButton}`}
                        text="Clear list"
                        icon={<IconTrash className={cn.clearListIcon} />}
                        onClick={() =>
                          setClearListPopupVisible(!clearListPopupVisible)
                        }
                      /> */}
                    </>
                  )}
                  {((!blacklist?.source?.includes('http') &&
                    activePermissions?.includes(
                      PermissionEnum.BLACKLISTS_EDIT_ACTION,
                    ) &&
                    blacklist?.link) ||
                    (selectedListIds && selectedListIds?.length > 0)) && (
                    <div className={cn.blacklistTopSeparator} />
                  )}
                  {selectedListIds && selectedListIds?.length > 0 && (
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      className={`button-white ${cn.blacklistPageControlButton}`}
                      text={`Delete ${selectedListIds?.length} record${
                        selectedListIds?.length > 1 ? 's' : ''
                      }`}
                      icon={<IconTrash className={cn.clearListIcon} />}
                      onClick={() =>
                        setDeleteRecordsPopupVisible(!deleteRecordsPopupVisible)
                      }
                    />
                  )}
                  {blacklist?.link && (
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      className={`button-white ${cn.blacklistPageControlButton}`}
                      text="Download list"
                      icon={<IconDownload />}
                    />
                  )}
                </div>
                <div className={cn.blacklistPageControlsRight}>
                  {activePermissions?.includes(
                    PermissionEnum.AUDIT_LOG_LIST,
                  ) && (
                    <NavLink
                      to={`${generatePath(ROUTES.BLACKLIST_ITEM_CHANGE_LOG, {
                        itemId: itemId as string,
                      })}${search || ''}`}
                      className={cn.changeLogLink}
                    >
                      <IconChangeLoClock />
                      Change Log
                    </NavLink>
                  )}
                  {/* <NavLink
                    to="#"
                    className={cn.blacklistPageDownloadTemplate}
                    onClick={downloadTemplate}
                  >
                    <IconDownload />
                    Template (csv)
                  </NavLink> */}
                  <NavLink
                    to={generatePath(
                      `${ROUTES.BLACKLIST_ITEM_SETTINGS}${search || ''}`,
                      { itemId: itemId as string },
                    )}
                    className={cn.blacklistSettingsLink}
                  >
                    <IconSettings />
                    List settings
                  </NavLink>
                </div>
              </div>
            </div>
            <div className={cn.blacklistContenWrapper}>
              <div className={cn.blacklistTableContent} ref={tableRef}>
                {takeCount === 0 && (
                  <div className={cn.blacklistTableLoader}>
                    <IconLoader />
                  </div>
                )}
                {takeCount !== 0 &&
                  activePermissions?.includes(
                    PermissionEnum.BLACKLISTS_ITEMS_LIST,
                  ) && (
                    <Table
                      columns={blacklistUsersColumns}
                      data={formattedData || []}
                      error={errorsList || errorList}
                      onSort={(name) => onSort(name)}
                      isLoading={
                        dataIsLoading || isFetching || blacklistIsFetching
                      }
                      filters={{}}
                      sort={sort}
                      // onRowsSelect={
                      //   !blacklist?.source?.includes('http')
                      //     ? (rows) => onRowsSelect(rows)
                      //     : undefined
                      // }
                      columnsLabels={{}}
                      onFilterChange={() => {}}
                      tableFormClassName={`${cn.tableForm} 
              ${!values?.length ? cn.emptyTable : ''}`}
                      tableBodyClassName={`${cn.blacklistPageTable}${
                        dataIsLoading || isFetching || blacklistIsFetching
                          ? cn.blacklistPageTableLoading
                          : ''
                      }`}
                      take={takeCount.toString()}
                      setPage={(data) => {
                        let nextSkip = skipValue as number;

                        if (Number(data.take) > 0) {
                          nextSkip += Number(data.take);
                        } else {
                          nextSkip -= Math.abs(Number(data.take));
                        }

                        dispatch(setSkipValue(nextSkip));

                        setPage({
                          ...data,
                          id: nextSkip?.toString(),
                          filters: {
                            ...filters,
                            take: {
                              value: [takeCount.toString()],
                              type: SchemaFilterType.STRING,
                            },
                          },
                          withSkip: true,
                        });
                      }}
                      pagination={pagination}
                      clickable
                    />
                  )}
                {!activePermissions?.includes(
                  PermissionEnum.BLACKLISTS_ITEMS_LIST,
                ) && <PermissionDenied />}
              </div>
            </div>
          </>
        )}
        {errors && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errors}</div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cn.blacklistPage}>
      <Popup
        isVisible={popupVisible}
        onCancelClick={() => setPopupVisible(false)}
        popupClassName={cn.uploadSuccessPopup}
      >
        <>
          <div className={cn.uploadSuccessPopupTitle}>
            Done
            <IconSelected className={cn.popupIcon} />
          </div>
          <div className={cn.uploadSuccessPopupDescription}>
            {`uploaded ${uploadedRows} rows`}
          </div>
          <ButtonSecondary
            text="Close"
            className={`button-white ${cn.uploadSuccessPopupButton}`}
            onClick={onCloseUploadPopup}
          />
        </>
      </Popup>
      <div className={cn.blacklistPageLinkWrapper}>
        <NavLink
          to={ROUTES.BLACKLIST_LIST}
          className={cn.blacklistPageBackLink}
          onClick={() => dispatch(clearBlacklistState())}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
      </div>
      {content}
      <Outlet context={{ getBlacklistAction }} />
      <Popup
        title="Clear the entire list?"
        description="Are you sure you want to remove all records from this list? This action cannot be undone."
        popupClassName={cn.deleteRecordPopup}
        onCancelClick={() => setClearListPopupVisible(false)}
        onOkClick={() => setClearListPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText="Clear list"
        isVisible={clearListPopupVisible}
      />
      <Popup
        title="Delete selected records?"
        description="Are you sure you want to delete the selected records? This action cannot be undone."
        popupClassName={cn.deleteRecordPopup}
        onCancelClick={() => setDeleteRecordsPopupVisible(false)}
        onOkClick={() => setDeleteRecordsPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText="Delete"
        isVisible={deleteRecordsPopupVisible}
      />
      <Popup
        title="Import records list"
        onCancelClick={() => setDropzoneVisible(false)}
        cancelButtonText="Cancel"
        isVisible={dropzoneVisible}
      >
        <>
          <Dropzone
            inputRef={inputRef}
            onUpload={onUpload}
            accept=".csv, text/xml"
            dropzoneIcon={
              <IconDropzoneAlternative className={cn.dropzoneIcon} />
            }
            fileUploadInProgress={uploadInProgress}
            dropzoneClassName={cn.dropzone}
          >
            <div className={cn.dropzoneContent}>
              <div className={cn.dropzoneContentText}>
                Drag and drop your CSV or XML file here
              </div>
              <div className={cn.dropzoneContentHint}>
                or choose a file from your computer
              </div>
            </div>
          </Dropzone>
          {values && values?.length > 0 && (
            <div className={cn.chooseUploadTypeWrapper}>
              <div className={cn.chooseUploadTypeTitle}>
                Choose how the imported data should be applied.
              </div>
              <div className={cn.chooseUploadTypeContent}>
                <div className={cn.chooseUploadTypeContentBlock}>
                  <Checkbox
                    name="new-type"
                    type="radio"
                    checked={selectedUploadType === 'new'}
                    title="Add new records"
                    onChange={() => setSelectedUploadType('new')}
                  />
                  <div className={cn.chooseUploadTypeContentBlockDescription}>
                    Add the records from the file to the existing list.
                  </div>
                </div>
                <div className={cn.chooseUploadTypeContentBlock}>
                  <Checkbox
                    name="replace-type"
                    type="radio"
                    checked={selectedUploadType === 'replace'}
                    title="Replace current records"
                    onChange={() => setSelectedUploadType('replace')}
                  />
                  <div className={cn.chooseUploadTypeContentBlockDescription}>
                    Remove all existing records and replace them with the
                    records from the file.
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      </Popup>
    </div>
  );
};

export default BlacklistItemPage;
